import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  createQuarterTorusGeometry,
  planeGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoPipeElbow } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A Rich Asset Model Pipe, plotted on a cartesian plane, with the center of its
 * base at "position" and decorated with Rich Asset features specific to pipes.
 * @param model               The pipe section component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const PipeElbowSurface3D: React.FC<
  { model: GeckoPipeElbow } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale } = React.useMemo(() => {
    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? new Vector3(1, 1, 1) // No additional scaling needed in 3D mode
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )

    return {
      bounds: model.componentBounds,
      scale: scale,
    }
  }, [model, show3D, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return createQuarterTorusGeometry(
      model.majorRadius.num, // Distance from center to tube center
      model.radius.num, // Tube radius
      model.angle // Angle in degrees
    )
  }, [show3D, model.majorRadius.num, model.radius.num, model.angle])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}></ModelSurface3D>
  )
}
